import { useEffect, useMemo, useState } from "react";
import "../../styles/design-system.css";
import "../features.css";
import { Badge } from "../../components/ui/Badge";
import { Tag } from "../../components/ui/Tag";
import { track } from "../../utils/analytics";
import { useFeatureView } from "../../utils/useFeatureView";

// ── Data source constants ──────────────────────────────────────────────────

const SOURCE = {
  INTAKE:    "Self-reported intake",
  REGISTRY:  "Registry (ClinicalTrials.gov)",
  PRAPARE:   "PRAPARE screening",
  MANUAL:    "Manual entry",
};

// ── Trial registry data ────────────────────────────────────────────────────
// Phase 1 source: ClinicalTrials.gov public registry.
// Eligibility criteria are parsed from NCT records; clinical thresholds
// are those stated in the public protocol — not extracted from EHR.

const TRIALS = [
  {
    id: "NCT-04211220",
    name: "CARDIO-Equity Phase II",
    sponsor: "Lilly",
    indication: "Type 2 diabetes + CVD risk",
    lastSynced: "2026-05-08",
    syncSource: SOURCE.REGISTRY,
    criteria: {
      ageMin: 40, ageMax: 75,
      conditions: ["type2-diabetes", "hypertension"],
      a1cMin: 7.0, a1cMax: 10.5,
      egfrMin: 30,
      excludesActiveCancer: true,
    },
  },
  {
    id: "NCT-05122987",
    name: "FOOD-Rx Pilot",
    sponsor: "Novo Nordisk",
    indication: "T2D + food insecurity",
    lastSynced: "2026-04-15",
    syncSource: SOURCE.REGISTRY,
    criteria: {
      ageMin: 18, ageMax: 80,
      conditions: ["type2-diabetes"],
      a1cMin: 6.5, a1cMax: 12.0,
      egfrMin: 25,
      requiresFoodInsecurity: true,
    },
  },
  {
    id: "NCT-06013344",
    name: "HTN-Community Adapt",
    sponsor: "AstraZeneca",
    indication: "Resistant hypertension",
    lastSynced: "2026-05-01",
    syncSource: SOURCE.REGISTRY,
    criteria: {
      ageMin: 30, ageMax: 85,
      conditions: ["hypertension"],
      sbpMin: 140,
      egfrMin: 30,
    },
  },
  {
    id: "NCT-04877655",
    name: "GLP-1 Equity Cohort",
    sponsor: "Lilly",
    indication: "T2D + obesity",
    lastSynced: "2026-05-09",
    syncSource: SOURCE.REGISTRY,
    criteria: {
      ageMin: 21, ageMax: 70,
      conditions: ["type2-diabetes", "obesity"],
      bmiMin: 30, a1cMin: 7.5,
    },
  },
  {
    id: "NCT-05550112",
    name: "CKD-Community",
    sponsor: "Merck",
    indication: "Diabetic kidney disease",
    lastSynced: "2026-03-10",
    syncSource: SOURCE.REGISTRY,
    criteria: {
      ageMin: 35, ageMax: 80,
      conditions: ["type2-diabetes", "ckd"],
      egfrMin: 20, egfrMax: 60,
    },
  },
];

// ── Patient cohort data ────────────────────────────────────────────────────
// Phase 1 source: patient-reported intake form + community health network
// referral. Clinical values (A1C, BMI, SBP, eGFR) are self-reported by the
// patient at intake — not extracted from an EHR. Site and condition data
// are confirmed via FQHC referral and registry cross-check.
//
// Phase 2 (roadmap): FHIR R4 integration with Epic / Cerner replaces
// self-reported values with verified lab results and vitals.

const PATIENTS = [
  {
    id: "GTH-8821",
    mrn: "GTH-8821",
    dataSource: "registry+self-report",
    dataPhase: 1,
    cohortPulledAt: "2026-05-09",
    name: "Maria T.",
    dob: "1966-03-14",
    age: 58,
    sex: "F",
    race: "White",
    ethnicity: "Hispanic or Latino",
    language: "Spanish",
    phone: "(617) 555-0142",
    email: "—",
    address: "Dorchester, MA 02125",
    insurance: "MassHealth",
    site: "Mt. Auburn FQHC",
    provider: "Dr. R. Patel, MD",
    coordinator: "J. Santos, CHW",
    conditions: ["type2-diabetes", "hypertension"],
    conditionsSource: SOURCE.REGISTRY,
    a1c: 8.4,   a1cSource: SOURCE.INTAKE,
    bmi: 32,    bmiSource: SOURCE.INTAKE,
    sbp: 152,   sbpSource: SOURCE.INTAKE,
    egfr: 62,   egfrSource: SOURCE.INTAKE,
    foodInsecure: true, foodInsecureSource: SOURCE.PRAPARE,
    lastVisit: "2026-04-18",
  },
  {
    id: "GTH-8907",
    mrn: "GTH-8907",
    dataSource: "registry+self-report",
    dataPhase: 1,
    cohortPulledAt: "2026-05-09",
    name: "James W.",
    dob: "1957-09-02",
    age: 67,
    sex: "M",
    race: "Black or African American",
    ethnicity: "Non-Hispanic",
    language: "English",
    phone: "(617) 555-0389",
    email: "j.w@email.com",
    address: "Roxbury, MA 02119",
    insurance: "Medicare",
    site: "Roxbury Health",
    provider: "Dr. A. Mensah, MD",
    coordinator: "T. Brown, RN",
    conditions: ["hypertension"],
    conditionsSource: SOURCE.REGISTRY,
    a1c: null,  a1cSource: null,
    bmi: 28,    bmiSource: SOURCE.INTAKE,
    sbp: 168,   sbpSource: SOURCE.INTAKE,
    egfr: 71,   egfrSource: SOURCE.INTAKE,
    foodInsecure: false, foodInsecureSource: SOURCE.PRAPARE,
    lastVisit: "2026-03-29",
  },
  {
    id: "GTH-9114",
    mrn: "GTH-9114",
    dataSource: "registry+self-report",
    dataPhase: 1,
    cohortPulledAt: "2026-05-09",
    name: "Priya S.",
    dob: "1981-11-22",
    age: 44,
    sex: "F",
    race: "Asian",
    ethnicity: "Non-Hispanic",
    language: "English",
    phone: "(617) 555-0571",
    email: "p.s@email.com",
    address: "Codman Square, MA 02124",
    insurance: "Connector Care",
    site: "Codman Square",
    provider: "Dr. L. Kim, MD",
    coordinator: "M. Rivera, CHW",
    conditions: ["type2-diabetes", "obesity"],
    conditionsSource: SOURCE.REGISTRY,
    a1c: 9.1,   a1cSource: SOURCE.INTAKE,
    bmi: 36,    bmiSource: SOURCE.INTAKE,
    sbp: 132,   sbpSource: SOURCE.INTAKE,
    egfr: 88,   egfrSource: SOURCE.INTAKE,
    foodInsecure: true, foodInsecureSource: SOURCE.PRAPARE,
    lastVisit: "2026-05-01",
  },
];

const ALL_CONDITIONS = [
  { id: "type2-diabetes", label: "T2D" },
  { id: "hypertension",   label: "Hypertension" },
  { id: "obesity",        label: "Obesity" },
  { id: "ckd",            label: "CKD" },
  { id: "heart-failure",  label: "Heart failure" },
];

// ── Freshness helpers ──────────────────────────────────────────────────────

function daysSince(dateStr) {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  return Math.floor(diff / 86_400_000);
}

function freshness(dateStr) {
  const d = daysSince(dateStr);
  if (d === null) return { level: "warn", label: "unknown" };
  if (d <= 7)  return { level: "ok",    label: d === 0 ? "today" : `${d}d ago` };
  if (d <= 30) return { level: "ok",    label: `${d}d ago` };
  if (d <= 60) return { level: "warn",  label: `${d}d ago · verify` };
  return       { level: "stale",        label: `${d}d ago · stale` };
}

// ── Matching engine ────────────────────────────────────────────────────────
// Returns enriched checks with patient value + data source for each
// criterion — required for FDA CDS non-device positioning (clinician
// must be able to review the basis of every flag).

function matchPatientToTrial(patient, trial) {
  const checks = [];
  const c = trial.criteria;

  if (c.ageMin != null && c.ageMax != null) {
    checks.push({
      label:        `Age ${c.ageMin}–${c.ageMax} yr`,
      patientValue: `${patient.age} yr`,
      source:       SOURCE.INTAKE,
      pass:         patient.age >= c.ageMin && patient.age <= c.ageMax,
    });
  }

  if (c.conditions && c.conditions.length > 0) {
    const ok = c.conditions.every((cond) => patient.conditions.includes(cond));
    checks.push({
      label:        `Conditions: ${c.conditions.join(", ")}`,
      patientValue: patient.conditions.length ? patient.conditions.join(", ") : "none on file",
      source:       patient.conditionsSource ?? SOURCE.REGISTRY,
      pass:         ok,
    });
  }

  if (c.a1cMin != null) {
    const v = patient.a1c;
    const range = `${c.a1cMin}${c.a1cMax != null ? `–${c.a1cMax}` : "+"}%`;
    checks.push({
      label:        `A1C ${range}`,
      patientValue: v != null ? `${v}%` : "not reported",
      source:       v != null ? (patient.a1cSource ?? SOURCE.INTAKE) : "—",
      pass:         v != null && v >= c.a1cMin && (c.a1cMax == null || v <= c.a1cMax),
    });
  }

  if (c.bmiMin != null) {
    checks.push({
      label:        `BMI ≥ ${c.bmiMin}`,
      patientValue: patient.bmi != null ? String(patient.bmi) : "not reported",
      source:       patient.bmi != null ? (patient.bmiSource ?? SOURCE.INTAKE) : "—",
      pass:         patient.bmi != null && patient.bmi >= c.bmiMin,
    });
  }

  if (c.sbpMin != null) {
    checks.push({
      label:        `SBP ≥ ${c.sbpMin} mmHg`,
      patientValue: patient.sbp != null ? `${patient.sbp} mmHg` : "not reported",
      source:       patient.sbp != null ? (patient.sbpSource ?? SOURCE.INTAKE) : "—",
      pass:         patient.sbp != null && patient.sbp >= c.sbpMin,
    });
  }

  if (c.egfrMin != null) {
    const range = c.egfrMax != null ? `${c.egfrMin}–${c.egfrMax}` : `≥${c.egfrMin}`;
    checks.push({
      label:        `eGFR ${range}`,
      patientValue: patient.egfr != null ? String(patient.egfr) : "not reported",
      source:       patient.egfr != null ? (patient.egfrSource ?? SOURCE.INTAKE) : "—",
      pass:         patient.egfr != null &&
                    patient.egfr >= c.egfrMin &&
                    (c.egfrMax == null || patient.egfr <= c.egfrMax),
    });
  }

  if (c.requiresFoodInsecurity) {
    checks.push({
      label:        "Food insecurity (SDOH)",
      patientValue: patient.foodInsecure ? "Screened positive" : "Not flagged",
      source:       patient.foodInsecureSource ?? SOURCE.PRAPARE,
      pass:         patient.foodInsecure === true,
    });
  }

  if (checks.length === 0) {
    return {
      trial, score: 100, eligible: true,
      checks: [{ label: "No criteria specified", patientValue: "—", source: "—", pass: true }],
    };
  }

  const passed = checks.filter((ch) => ch.pass).length;
  const score  = Math.round((passed / checks.length) * 100);
  return { trial, checks, score, eligible: passed === checks.length };
}

// ── Text extraction (unchanged) ────────────────────────────────────────────

function extractFromText(text) {
  const lo = text.toLowerCase();
  const out = {};

  const ageRange = lo.match(/(?:age[sd]?\s+(?:between\s+)?|aged?\s+)(\d+)\s*(?:to|[-–—])\s*(\d+)\s*years?/);
  if (ageRange) { out.ageMin = ageRange[1]; out.ageMax = ageRange[2]; }
  else {
    const aMin = lo.match(/(?:age[sd]?\s*(?:≥|>=|at least|of at least)\s*|minimum\s+age[^0-9]*|(\d+)\s+years?\s+(?:of age\s+)?or older)(\d+)/);
    if (aMin) out.ageMin = (aMin[1] || aMin[2]) ?? "";
    const aMax = lo.match(/(?:age[sd]?\s*(?:≤|<=|no more than|up to)\s*|maximum\s+age[^0-9]*|(\d+)\s+years?\s+(?:of age\s+)?or younger)(\d+)/);
    if (aMax) out.ageMax = (aMax[1] || aMax[2]) ?? "";
  }
  if (!out.ageMin) { const m = lo.match(/(?:≥|>=)\s*(\d+)\s*years/); if (m) out.ageMin = m[1]; }
  if (!out.ageMax) { const m = lo.match(/(?:≤|<=)\s*(\d+)\s*years/); if (m) out.ageMax = m[1]; }

  const a1cRange = lo.match(/(?:hba1c?|a1c|hemoglobin a1c?)\s*(?:of\s+)?(\d+\.?\d*)\s*(?:to|[-–—])\s*(\d+\.?\d*)/);
  if (a1cRange) { out.a1cMin = a1cRange[1]; out.a1cMax = a1cRange[2]; }
  else {
    const m1 = lo.match(/(?:hba1c?|a1c)\s*(?:≥|>=|of at least|at least|greater than or equal to)\s*(\d+\.?\d*)/);
    if (m1) out.a1cMin = m1[1];
    const m2 = lo.match(/(?:hba1c?|a1c)\s*(?:≤|<=|no more than|less than or equal to)\s*(\d+\.?\d*)/);
    if (m2) out.a1cMax = m2[1];
  }

  const bmi = lo.match(/bmi\s*(?:≥|>=|of at least|at least|>|of\s+at\s+least)\s*(\d+\.?\d*)/);
  if (bmi) out.bmiMin = bmi[1];

  const sbp = lo.match(/(?:systolic\s+blood\s+pressure|sbp)\s*(?:≥|>=|of at least|at least|>)\s*(\d+)/);
  if (sbp) out.sbpMin = sbp[1];

  const egfrRange = lo.match(/egfr\s*(?:of\s+)?(\d+)\s*(?:to|[-–—])\s*(\d+)/);
  if (egfrRange) { out.egfrMin = egfrRange[1]; out.egfrMax = egfrRange[2]; }
  else {
    const m1 = lo.match(/egfr\s*(?:≥|>=|at least|of at least|>)\s*(\d+)/); if (m1) out.egfrMin = m1[1];
    const m2 = lo.match(/egfr\s*(?:≤|<=|no more than|<)\s*(\d+)/);         if (m2) out.egfrMax = m2[1];
  }

  const conds = [];
  if (/type\s*[2ii]\s*diabet|t2d\b|type 2 diabete|diabetes mellitus type 2/.test(lo)) conds.push("type2-diabetes");
  if (/hypertension|high blood pressure/.test(lo)) conds.push("hypertension");
  if (/\bobesit|\bobese\b/.test(lo)) conds.push("obesity");
  if (/chronic kidney disease|ckd\b|diabetic nephropathy/.test(lo)) conds.push("ckd");
  if (/heart failure|cardiac failure/.test(lo)) conds.push("heart-failure");
  if (conds.length) out.conditions = conds;

  if (/food.?insecur|snap eligib/.test(lo)) out.requiresFoodInsecurity = true;

  return out;
}

// ── Draft helpers ─────────────────────────────────────────────────────────

const BLANK_DRAFT = {
  name: "", nct: "", sponsor: "", indication: "",
  rawText: "",
  ageMin: "", ageMax: "",
  conditions: [],
  a1cMin: "", a1cMax: "",
  bmiMin: "", sbpMin: "",
  egfrMin: "", egfrMax: "",
  requiresFoodInsecurity: false,
};

function draftToTrial(draft) {
  const num = (v) => (v !== "" && !isNaN(+v) ? +v : null);
  return {
    id: `custom-${Date.now()}`,
    name: draft.name.trim() || "Unnamed trial",
    nct: draft.nct.trim() || null,
    sponsor: draft.sponsor.trim() || "—",
    indication: draft.indication.trim() || "—",
    custom: true,
    lastSynced: new Date().toISOString().slice(0, 10),
    syncSource: SOURCE.MANUAL,
    criteria: {
      ageMin: num(draft.ageMin), ageMax: num(draft.ageMax),
      conditions: draft.conditions,
      a1cMin: num(draft.a1cMin), a1cMax: num(draft.a1cMax),
      bmiMin: num(draft.bmiMin), sbpMin: num(draft.sbpMin),
      egfrMin: num(draft.egfrMin), egfrMax: num(draft.egfrMax),
      requiresFoodInsecurity: draft.requiresFoodInsecurity,
    },
  };
}

// NCT submission schema template (shown in the "Add your trial" guide)
const NCT_SCHEMA_TEMPLATE = `{
  "nctId":      "NCT12345678",
  "name":       "Trial display name",
  "sponsor":    "Sponsor organization",
  "indication": "e.g. T2D + CVD risk",
  "eligibility": {
    "ageMin":               18,
    "ageMax":               75,
    "a1cMin":               7.0,
    "a1cMax":               11.0,
    "bmiMin":               null,
    "sbpMin":               null,
    "egfrMin":              30,
    "egfrMax":              null,
    "conditions":           ["type2-diabetes"],
    "requiresFoodInsecurity": false
  }
}`;

// ── Component ─────────────────────────────────────────────────────────────

export function TrialMatching() {
  const sectionRef = useFeatureView("trial_matching");
  const [selectedId,   setSelectedId]   = useState(PATIENTS[0].id);
  const [customTrials, setCustomTrials] = useState([]);
  const [addingTrial,  setAddingTrial]  = useState(false);
  const [draft,        setDraft]        = useState(BLANK_DRAFT);
  const [extracted,    setExtracted]    = useState(false);
  const [showSchema,   setShowSchema]   = useState(false);

  const patient  = PATIENTS.find((p) => p.id === selectedId) ?? PATIENTS[0];
  const allTrials = useMemo(() => [...TRIALS, ...customTrials], [customTrials]);

  const matches = useMemo(
    () => allTrials.map((t) => matchPatientToTrial(patient, t)).sort((a, b) => b.score - a.score),
    [patient, allTrials]
  );

  useEffect(() => {
    const eligible = matches.filter((m) => m.eligible).length;
    track("trial_match_interaction", {
      patientSite: patient.site,
      conditions:  patient.conditions,
      eligibleCount: eligible,
      topScore: matches[0]?.score ?? 0,
      totalTrials: matches.length,
    });
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  function set(field, value) { setDraft((prev) => ({ ...prev, [field]: value })); }

  function toggleCondition(id) {
    setDraft((prev) => ({
      ...prev,
      conditions: prev.conditions.includes(id)
        ? prev.conditions.filter((c) => c !== id)
        : [...prev.conditions, id],
    }));
  }

  function handleExtract() {
    const result = extractFromText(draft.rawText);
    setDraft((prev) => ({
      ...prev,
      ageMin: result.ageMin ?? prev.ageMin,
      ageMax: result.ageMax ?? prev.ageMax,
      conditions: result.conditions ?? prev.conditions,
      a1cMin: result.a1cMin ?? prev.a1cMin, a1cMax: result.a1cMax ?? prev.a1cMax,
      bmiMin: result.bmiMin ?? prev.bmiMin, sbpMin: result.sbpMin ?? prev.sbpMin,
      egfrMin: result.egfrMin ?? prev.egfrMin, egfrMax: result.egfrMax ?? prev.egfrMax,
      requiresFoodInsecurity: result.requiresFoodInsecurity ?? prev.requiresFoodInsecurity,
    }));
    setExtracted(true);
    track("trial_custom_extract");
  }

  function handleAdd() {
    const trial = draftToTrial(draft);
    setCustomTrials((prev) => [...prev, trial]);
    setDraft(BLANK_DRAFT);
    setAddingTrial(false);
    setExtracted(false);
    setShowSchema(false);
    track("trial_custom_add", { name: trial.name });
  }

  function handleCancel() {
    setDraft(BLANK_DRAFT);
    setAddingTrial(false);
    setExtracted(false);
    setShowSchema(false);
  }

  function removeCustomTrial(id) {
    setCustomTrials((prev) => prev.filter((t) => t.id !== id));
  }

  const pf = freshness(patient.cohortPulledAt);

  return (
    <section ref={sectionRef} className="ds-section feature-section" id="feature-trial-matching">
      <div className="ds-page">
        <div className="ds-section-head">
          <div className="ds-section-num">01 / 06</div>
          <div>
            <h2 className="ds-section-title">AI trial matching</h2>
            <p className="ds-section-sub">
              Helps your care team identify eligible patients from your existing
              panel using registry criteria and patient-reported eligibility.
              Clinicians review every flag and decide whether to initiate contact.
              Paste in any trial to run it against this cohort.
            </p>
          </div>
        </div>

        {/* ── Architecture tier banner ── */}
        <div className="tm-arch-banner">
          <div className="tm-arch-phase tm-arch-phase--active">
            <div className="tm-arch-phase-label">Phase 1 · Now</div>
            <div className="tm-arch-phase-title">Registry + Self-reported intake</div>
            <div className="tm-arch-phase-sources">
              Trial criteria from ClinicalTrials.gov · Patient values from intake form ·
              Conditions confirmed via FQHC referral + registry · SDOH via PRAPARE
            </div>
          </div>
          <div className="tm-arch-phase tm-arch-phase--future">
            <div className="tm-arch-phase-label tm-arch-phase-label--future">Phase 2 · Roadmap</div>
            <div className="tm-arch-phase-title">FHIR R4 EHR Integration</div>
            <div className="tm-arch-phase-sources">
              Lab values from Epic / Cerner FHIR R4 · Automated eligibility screening ·
              Real-time cohort refresh · Requires site BAA + FHIR app registration
            </div>
          </div>
        </div>

        {/* Patient selector */}
        <div className="ds-card-label">
          <span>PATIENT · REGISTRY + SELF-REPORT</span>
          <span>SELECT FROM COHORT</span>
        </div>
        <div className="tm-cohort-pull">
          <span>Cohort pulled</span>
          <span className={`tm-freshness tm-freshness--${pf.level}`}>
            <span className="tm-freshness-dot" aria-hidden="true" />
            {pf.label}
          </span>
          <span>· {PATIENTS.length} patients · Phase 1 data</span>
        </div>
        <div className="feature-pill-row">
          {PATIENTS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`feature-pill${p.id === selectedId ? " feature-pill--active" : ""}`}
              onClick={() => setSelectedId(p.id)}
            >
              {p.name} <span className="s">/</span> {p.site}
            </button>
          ))}
        </div>

        {/* Patient summary + rationale */}
        <div className="ds-grid-2 feature-spaced">
          <div className="ds-card">
            <div className="ds-card-label">
              <span>PATIENT · {patient.mrn}</span>
              <span>LAST VISIT {patient.lastVisit}</span>
            </div>

            <div className="tm-patient-name">{patient.name}</div>
            <div className="tm-patient-sub">
              {patient.dob} · Age {patient.age} · {patient.sex} · {patient.race}
              {patient.ethnicity !== "Non-Hispanic" ? ` · ${patient.ethnicity}` : ""}
            </div>

            <div className="tm-patient-section-label">CONTACT</div>
            <div className="feature-kv">
              <div><dt>Phone</dt><dd>{patient.phone}</dd></div>
              <div><dt>Email</dt><dd>{patient.email}</dd></div>
              <div><dt>Address</dt><dd>{patient.address}</dd></div>
              <div><dt>Language</dt><dd>{patient.language}</dd></div>
            </div>

            <div className="tm-patient-section-label">CARE TEAM</div>
            <div className="feature-kv">
              <div><dt>Site</dt><dd>{patient.site}</dd></div>
              <div><dt>Insurance</dt><dd>{patient.insurance}</dd></div>
              <div><dt>Provider</dt><dd>{patient.provider}</dd></div>
              <div><dt>Coordinator</dt><dd>{patient.coordinator}</dd></div>
            </div>

            <div className="tm-patient-section-label">
              CLINICAL · SOURCE: {patient.a1cSource ?? "SELF-REPORTED INTAKE"}
            </div>
            <div className="feature-kv">
              <div>
                <dt>Conditions</dt>
                <dd>{patient.conditions.join(", ")}</dd>
              </div>
              <div>
                <dt>Food insecure</dt>
                <dd>{patient.foodInsecure ? "Yes" : "No"} <span style={{ color: "var(--gray-7)", fontSize: 10 }}>({patient.foodInsecureSource})</span></dd>
              </div>
              <div>
                <dt>A1C</dt>
                <dd>{patient.a1c != null ? `${patient.a1c}%` : <span style={{ color: "var(--gray-7)" }}>not reported</span>}</dd>
              </div>
              <div>
                <dt>BMI</dt>
                <dd>{patient.bmi ?? <span style={{ color: "var(--gray-7)" }}>not reported</span>}</dd>
              </div>
              <div>
                <dt>SBP</dt>
                <dd>{patient.sbp != null ? `${patient.sbp} mmHg` : <span style={{ color: "var(--gray-7)" }}>not reported</span>}</dd>
              </div>
              <div>
                <dt>eGFR</dt>
                <dd>{patient.egfr ?? <span style={{ color: "var(--gray-7)" }}>not reported</span>}</dd>
              </div>
            </div>
          </div>

          <div className="ds-card">
            <div className="ds-card-label">
              <span>EXPLAINABILITY · DATA SOURCES</span>
              <span>TRANSPARENT SCORING</span>
            </div>
            <p className="feature-help" style={{ marginBottom: 14 }}>
              Every match shows the patient's actual value and its data source against
              each trial criterion. Clinicians review this audit trail before deciding
              whether to initiate contact — no criterion is a black box.
            </p>
            <table className="ds-spec-table" style={{ marginBottom: 0 }}>
              <tbody>
                <tr>
                  <td>Scoring</td>
                  <td>Share of stated eligibility criteria met</td>
                </tr>
                <tr>
                  <td>Patient values</td>
                  <td>Self-reported intake form (Phase 1)</td>
                </tr>
                <tr>
                  <td>Trial criteria</td>
                  <td>ClinicalTrials.gov public registry</td>
                </tr>
                <tr>
                  <td>SDOH</td>
                  <td>PRAPARE screening at FQHC visit</td>
                </tr>
                <tr>
                  <td>Output</td>
                  <td>"Flag for review" — clinician makes final determination</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Ranked matches */}
        <div className="feature-spaced">
          <div className="ds-card-label" style={{ marginBottom: 12 }}>
            <span>RANKED MATCHES · {allTrials.length} TRIALS</span>
            <span>{matches.filter((m) => m.eligible).length} / {matches.length} ELIGIBLE</span>
          </div>

          {!addingTrial && (
            <button type="button" className="tm-add-btn" onClick={() => setAddingTrial(true)}>
              <span className="tm-add-btn-slash">/</span> Add your trial
            </button>
          )}

          {/* Custom trial editor */}
          {addingTrial && (
            <div className="tm-editor ds-card">
              <div className="tm-editor-header">
                <span className="tm-editor-title">
                  <span className="tm-editor-slash">/</span> Add a custom trial
                </span>
                <button type="button" className="tm-editor-close" onClick={handleCancel} aria-label="Cancel">✕</button>
              </div>

              <div className="tm-field-grid tm-field-grid--4">
                <label className="ds-input-label">
                  Trial name
                  <input type="text" className="ds-input" placeholder="e.g. CARDIO-Equity Phase II"
                    value={draft.name} onChange={(e) => set("name", e.target.value)} />
                </label>
                <label className="ds-input-label">
                  NCT number
                  <input type="text" className="ds-input" placeholder="NCT00000000"
                    value={draft.nct} onChange={(e) => set("nct", e.target.value)} />
                </label>
                <label className="ds-input-label">
                  Sponsor
                  <input type="text" className="ds-input" placeholder="e.g. Novo Nordisk"
                    value={draft.sponsor} onChange={(e) => set("sponsor", e.target.value)} />
                </label>
                <label className="ds-input-label">
                  Indication
                  <input type="text" className="ds-input" placeholder="e.g. T2D + obesity"
                    value={draft.indication} onChange={(e) => set("indication", e.target.value)} />
                </label>
              </div>

              {/* Schema guide */}
              <div style={{ marginBottom: 12 }}>
                <button type="button" className="tm-schema-toggle" onClick={() => setShowSchema((v) => !v)}>
                  {showSchema ? "▲" : "▼"} GTH submission schema
                </button>
                {showSchema && (
                  <div className="tm-schema-guide">
                    <div className="ds-card-label" style={{ marginBottom: 8 }}>REQUIRED FIELDS · JSON FORMAT</div>
                    <pre className="tm-schema-json">{NCT_SCHEMA_TEMPLATE}</pre>
                    <div className="tm-schema-note">
                      <strong>Required:</strong> nctId, name, sponsor, indication, eligibility.ageMin, eligibility.ageMax, eligibility.conditions.<br />
                      <strong>Optional:</strong> a1cMin/Max, bmiMin, sbpMin, egfrMin/Max, requiresFoodInsecurity — leave null if not specified in protocol.<br />
                      <strong>Processing:</strong> Submit NCT ID or paste JSON above. GTH syncs criteria within 24 hours and notifies your coordinator.
                    </div>
                  </div>
                )}
              </div>

              {/* Paste + extract */}
              <div className="tm-paste-section">
                <div className="tm-paste-label">
                  <span className="ds-card-label" style={{ marginBottom: 0 }}>PASTE ELIGIBILITY CRITERIA</span>
                  <span className="tm-paste-hint">from ClinicalTrials.gov or a protocol document</span>
                </div>
                <textarea
                  className="tm-paste-area"
                  rows={5}
                  placeholder={`Paste criteria text here — e.g.\n\nInclusion Criteria:\n  • Age 18–70 years\n  • Type 2 diabetes with HbA1c ≥ 7.5%\n  • BMI ≥ 28\n  • eGFR ≥ 30 mL/min`}
                  value={draft.rawText}
                  onChange={(e) => { set("rawText", e.target.value); setExtracted(false); }}
                />
                <div className="tm-paste-actions">
                  <button type="button" className="ds-btn ds-btn--slash"
                    disabled={!draft.rawText.trim()} onClick={handleExtract}>
                    Auto-extract fields ↓
                  </button>
                  {extracted && <span className="tm-extracted-badge">✓ Fields extracted — review below</span>}
                </div>
              </div>

              {/* Structured criteria */}
              <div className="tm-criteria-section">
                <div className="tm-criteria-label">ELIGIBILITY CRITERIA</div>
                <div className="tm-field-grid tm-field-grid--4">
                  <label className="ds-input-label">Age min
                    <input type="number" className="ds-input" placeholder="e.g. 18"
                      value={draft.ageMin} onChange={(e) => set("ageMin", e.target.value)} />
                  </label>
                  <label className="ds-input-label">Age max
                    <input type="number" className="ds-input" placeholder="e.g. 75"
                      value={draft.ageMax} onChange={(e) => set("ageMax", e.target.value)} />
                  </label>
                  <label className="ds-input-label">A1C min (%)
                    <input type="number" step="0.1" className="ds-input" placeholder="e.g. 7.0"
                      value={draft.a1cMin} onChange={(e) => set("a1cMin", e.target.value)} />
                  </label>
                  <label className="ds-input-label">A1C max (%)
                    <input type="number" step="0.1" className="ds-input" placeholder="e.g. 12.0"
                      value={draft.a1cMax} onChange={(e) => set("a1cMax", e.target.value)} />
                  </label>
                  <label className="ds-input-label">BMI min
                    <input type="number" step="0.5" className="ds-input" placeholder="e.g. 30"
                      value={draft.bmiMin} onChange={(e) => set("bmiMin", e.target.value)} />
                  </label>
                  <label className="ds-input-label">SBP min (mmHg)
                    <input type="number" className="ds-input" placeholder="e.g. 140"
                      value={draft.sbpMin} onChange={(e) => set("sbpMin", e.target.value)} />
                  </label>
                  <label className="ds-input-label">eGFR min
                    <input type="number" className="ds-input" placeholder="e.g. 30"
                      value={draft.egfrMin} onChange={(e) => set("egfrMin", e.target.value)} />
                  </label>
                  <label className="ds-input-label">eGFR max
                    <input type="number" className="ds-input" placeholder="e.g. 60"
                      value={draft.egfrMax} onChange={(e) => set("egfrMax", e.target.value)} />
                  </label>
                </div>
                <div className="tm-conditions-row">
                  <div className="tm-criteria-label" style={{ marginBottom: 8 }}>REQUIRED CONDITIONS</div>
                  <div className="feature-pill-row" style={{ marginBottom: 0 }}>
                    {ALL_CONDITIONS.map((c) => {
                      const active = draft.conditions.includes(c.id);
                      return (
                        <button key={c.id} type="button"
                          className={`feature-pill${active ? " feature-pill--active" : ""}`}
                          onClick={() => toggleCondition(c.id)}>
                          {c.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="feature-checkboxes tm-checkbox-row">
                  <label>
                    <input type="checkbox" checked={draft.requiresFoodInsecurity}
                      onChange={(e) => set("requiresFoodInsecurity", e.target.checked)} />
                    Requires food insecurity (SDOH)
                  </label>
                </div>
              </div>

              <div className="tm-editor-actions">
                <button type="button" className="ds-btn ds-btn--slash" onClick={handleAdd}>
                  Add to matching →
                </button>
                <button type="button" className="ds-btn ds-btn--ghost" onClick={handleCancel}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Match list */}
          <div className="feature-match-list" style={{ marginTop: 16 }}>
            {matches.map((m) => {
              const sf = freshness(m.trial.lastSynced);
              return (
                <div key={m.trial.id}
                  className={`feature-match-row${m.trial.custom ? " feature-match-row--custom" : ""}`}>
                  <div className="feature-match-head">
                    <div className="feature-match-title">
                      {m.trial.name}{" "}
                      <Tag variant={m.eligible ? "slash" : "default"}>
                        {m.eligible ? "/ eligible" : "partial"}
                      </Tag>
                      {m.trial.custom && <Tag variant="default">custom</Tag>}
                    </div>
                    <div className="feature-match-meta">
                      {m.trial.sponsor} · {m.trial.nct ?? m.trial.id} · {m.trial.indication}
                      {" · "}
                      <span className={`tm-freshness tm-freshness--${sf.level}`}>
                        <span className="tm-freshness-dot" aria-hidden="true" />
                        {m.trial.syncSource} · {sf.label}
                      </span>
                    </div>
                  </div>
                  <div
                    className="feature-score-bar"
                    role="progressbar"
                    aria-valuenow={m.score}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`${m.trial.name} match score: ${m.score}%`}
                  >
                    <div className="feature-score-fill" style={{ width: `${m.score}%` }} />
                  </div>
                  <div className="feature-match-score">{m.score}%</div>
                  <div className="feature-match-status">
                    {m.trial.custom ? (
                      <button type="button" className="tm-remove-btn"
                        onClick={() => removeCustomTrial(m.trial.id)}
                        aria-label={`Remove ${m.trial.name}`}>
                        Remove
                      </button>
                    ) : (
                      <Badge status={m.eligible ? "live" : "draft"}>
                        {m.eligible ? "Flag for review" : "Not eligible"}
                      </Badge>
                    )}
                  </div>

                  {/* Enriched eligibility check rows */}
                  <div className="feature-match-checks">
                    <div className="feature-check-header" aria-hidden="true">
                      <span>Criterion</span>
                      <span>Patient value</span>
                      <span>Source</span>
                      <span />
                    </div>
                    {m.checks.map((c, i) => (
                      <div key={i} className="feature-check-row">
                        <span className="feature-check-criterion">{c.label}</span>
                        <span className="feature-check-value">{c.patientValue}</span>
                        <span className="feature-check-source">{c.source}</span>
                        <span className={`feature-check-mark feature-check-mark--${c.pass ? "pass" : "fail"}`}>
                          {c.pass ? "✓" : "✗"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

export default TrialMatching;
